import { femaApi } from 'services/ipaws/ipaws.api';
import type {
  FemaCredentialSlotName,
  FemaTenantSummary,
  FemaTenantValidateAnswer,
} from 'types/femaTenant.types';
import {
  FEMA_TENANTS_PATH,
  readFemaTenant,
  readFemaTenantEnableAnswer,
  readFemaTenantList,
  readFemaTenantRotateAnswer,
  readFemaTenantValidateAnswer,
} from './femaTenants.service';

/**
 * FE-11: the cacheable tenant reads and the small tenant commands, on the Fusion edge mirror
 * (MULTICOG-CONTRACT §5): `GET /tenants`, `GET /tenants/:id`, `POST /tenants/:id/validate`,
 * `POST /tenants/:id/enable`, `POST /tenants/:id/rotate`. The two multipart uploads live in
 * `femaTenants.service.ts`.
 *
 * The endpoints are injected into `femaApi`, so `store.js` does not change. Every command
 * invalidates `FemaTenant` and `FemaMeta`: a credential change can change what the Alert Creator
 * may send.
 *
 * Each endpoint reads its answer with the ONE reader of its route (`femaTenants.service.ts`). A
 * reader answers `null` for a malformed answer. The page shows the contract refusal for `null`.
 * The tests mock the base query.
 */

export type ValidateFemaTenantRequest = {
  tenantId: number;
  /** Contract §5: `active` for a first credential, `pending` for a replacement. */
  slot?: FemaCredentialSlotName;
};

const request = (url: string, method: 'GET' | 'POST', data?: Record<string, unknown>) => ({
  url,
  method,
  data,
  params: undefined,
  headers: undefined,
  responseType: undefined,
  msAuth: undefined,
});

export const femaTenantsApi = femaApi
  .enhanceEndpoints({ addTagTypes: ['FemaTenant'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** The held classes of the session workplace, one summary per class. `null` = malformed. */
      getFemaTenants: builder.query<FemaTenantSummary[] | null, void>({
        query: () => request(FEMA_TENANTS_PATH, 'GET'),
        transformResponse: (response: unknown) => readFemaTenantList(response),
        providesTags: ['FemaTenant'],
      }),
      getFemaTenant: builder.query<FemaTenantSummary | null, number>({
        query: (tenantId: number) => request(`${FEMA_TENANTS_PATH}/${tenantId}`, 'GET'),
        transformResponse: (response: unknown) => readFemaTenant(response),
        providesTags: ['FemaTenant'],
      }),
      /** Runs a FEMA handshake with the credential in the slot. Answers pass or fail and the stage. */
      validateFemaTenant: builder.mutation<
        FemaTenantValidateAnswer | null,
        ValidateFemaTenantRequest
      >({
        query: ({ tenantId, slot }: ValidateFemaTenantRequest) =>
          request(`${FEMA_TENANTS_PATH}/${tenantId}/validate`, 'POST', slot ? { slot } : {}),
        transformResponse: (response: unknown) => readFemaTenantValidateAnswer(response),
        invalidatesTags: ['FemaTenant', 'FemaMeta'],
      }),
      /** Enables a tenant whose first credential passed validation. */
      enableFemaTenant: builder.mutation<FemaTenantSummary | null, number>({
        query: (tenantId: number) => request(`${FEMA_TENANTS_PATH}/${tenantId}/enable`, 'POST', {}),
        transformResponse: (response: unknown) => readFemaTenantEnableAnswer(response),
        invalidatesTags: ['FemaTenant', 'FemaMeta'],
      }),
      /** Swaps the validated replacement in. The current certificate stays active until then. */
      rotateFemaTenant: builder.mutation<FemaTenantSummary | null, number>({
        query: (tenantId: number) => request(`${FEMA_TENANTS_PATH}/${tenantId}/rotate`, 'POST', {}),
        transformResponse: (response: unknown) => readFemaTenantRotateAnswer(response),
        invalidatesTags: ['FemaTenant', 'FemaMeta'],
      }),
    }),
  });

export const {
  useGetFemaTenantsQuery,
  useGetFemaTenantQuery,
  useValidateFemaTenantMutation,
  useEnableFemaTenantMutation,
  useRotateFemaTenantMutation,
} = femaTenantsApi;
